import type { FormFieldOption, ValidationRule } from '../types/formBuilder';

/**
 * Form Layout Utilities
 * 
 * Provides utilities for mapping form field gridWidth to Tailwind CSS classes.
 * Used by entity edit pages (Hospital, HCP, Patient) to render forms with
 * proper column widths as defined in the form schema.
 * 
 * Grid System:
 * - Uses CSS Grid with 12 columns (Material Design standard)
 * - Responsive: stacks on mobile, applies widths on md+ breakpoints
 * 
 * Width Mappings:
 * - 100% → col-span-12 (full width)
 * - 50% → col-span-6 (half width)
 * - 33% → col-span-4 (one-third width)
 * - 20% → col-span-3 (approximately one-fifth, using 3/12 for better fit)
 */

export type GridWidthType = '100%' | '50%' | '33%' | '20%';

/**
 * Maps gridWidth to Tailwind grid column span classes
 * Uses md: prefix for responsive design (stacks on mobile)
 * 
 * @param gridWidth - The width from form schema ('100%' | '50%' | '33%' | '20%')
 * @returns Tailwind CSS classes for column span
 */
export function getGridColSpan(gridWidth?: GridWidthType | string): string {
  switch (gridWidth) {
    case '50%':
      return 'col-span-12 md:col-span-6';
    case '33%':
      return 'col-span-12 md:col-span-4';
    case '20%':
      // 20% of 12 columns = 2.4, using 3 for better fit
      return 'col-span-12 md:col-span-3';
    case '100%':
    default:
      // Full width: spans all 12 columns
      return 'col-span-12';
  }
}

/**
 * Returns the CSS class for the form grid container.
 * Uses 12-column grid with responsive gap.
 */
export function getFormGridContainerClass(): string {
  return 'grid grid-cols-12 gap-3';
}

/**
 * FormFieldDefinition interface extended with gridWidth
 * Import this in edit pages to ensure gridWidth is recognized
 */
export interface FormFieldWithWidth {
  fieldId: string;
  label: string;
  internalName: string;
  fieldType: string;
  mandatory: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string | number | boolean | string[] | null;
  sortOrder?: number;
  options?: FormFieldOption[];
  validationRules?: ValidationRule[];
  properties?: Record<string, unknown>;
  /** Column width in the form's 12-column grid. Defaults to '100%'. */
  gridWidth?: GridWidthType | string;
  /** Layout direction for RADIO / DROPDOWN / MULTISELECT options */
  optionLayout?: 'stacked' | 'inline';
}

/**
 * Determines if a field type should always span full width
 * (e.g., Terms & Conditions, large text areas)
 */
export function isFullWidthFieldType(fieldType: string): boolean {
  const fullWidthTypes = [
    'TERMS_AND_CONDITIONS',
    'RICH_TEXT_DISPLAY',
    'SECTION_BREAK',
    'DIVIDER',
  ];
  return fullWidthTypes.includes(fieldType.toUpperCase());
}

/**
 * Gets the appropriate column span for a field, considering field type overrides
 */
export function getFieldColSpan(gridWidth?: GridWidthType | string, fieldType?: string): string {
  // Force full width for certain field types
  if (fieldType && isFullWidthFieldType(fieldType)) {
    return 'col-span-12';
  }
  return getGridColSpan(gridWidth);
}
